import React, { useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import { doc, updateDoc } from "firebase/firestore";
import { db } from '../../firebaseConfig';
import SmallButton from '../shared/SmallButton';
import { sendOneTimeLoginCodeByEmail, isEmailConfirmedForAnotherUser } from '../../services/users/Users';

export default function AddEmail({ onEmailAdded, onCancel }) {
  const [newEmail, setNewEmail] = useState('');
  const [isEmailCodeSent, setIsEmailCodeSent] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailVerificationMessage, setEmailVerificationMessage] = useState('');
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');

  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      setEmailVerificationMessage('Please enter an email address.');
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(newEmail)) {
      setEmailVerificationMessage('Please enter a valid email address.');
      return;
    }

    setIsVerifyingEmail(true);
    setEmailVerificationMessage('');
    
    try {
      // Check if email is already confirmed for another user
      const savedUserId = window.localStorage.getItem('userId');
      const emailAlreadyUsed = await isEmailConfirmedForAnotherUser(newEmail, savedUserId);
      
      if (emailAlreadyUsed) {
        setEmailVerificationMessage('This email address is already registered to another account.');
        setIsVerifyingEmail(false);
        return;
      }
      
      // Generate a 4 digit code
      const code = Math.floor(1000 + Math.random() * 9000);
      setGeneratedCode(code.toString());
      
      await sendOneTimeLoginCodeByEmail(newEmail, code);
      setIsEmailCodeSent(true);
      setEmailVerificationMessage('');
      setIsVerifyingEmail(false);
    } catch (error) {
      setEmailVerificationMessage('Error sending verification code. Please try again.');
      setIsVerifyingEmail(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    if (!emailVerificationCode.trim()) {
      setEmailVerificationMessage('Please enter the verification code.');
      return;
    }

    setIsVerifyingEmail(true);
    
    try {
      // Verify the code matches
      if (emailVerificationCode !== generatedCode) {
        setEmailVerificationMessage('Incorrect verification code.');
        setIsVerifyingEmail(false);
        return;
      }

      // Update user's email in the database
      const savedUserId = window.localStorage.getItem('userId');
      if (savedUserId) {
        const userRef = doc(db, "Users", savedUserId);
        await updateDoc(userRef, {
          email: newEmail,
          emailLowerCase: newEmail.toLowerCase(),
          isEmailConfirmed: true,
        });
        
        setEmailVerificationMessage('Email successfully added!');
        
        // Notify parent component and reset after a delay
        setTimeout(() => {
          onEmailAdded(newEmail);
          resetState();
        }, 2000);
      }
    } catch (error) {
      setEmailVerificationMessage('Error verifying code. Please try again.');
    }
    
    setIsVerifyingEmail(false);
  };

  const handleResendEmailCode = async () => {
    setIsVerifyingEmail(true);
    setEmailVerificationMessage('');
    
    try {
      // Generate a new 4 digit code
      const code = Math.floor(1000 + Math.random() * 9000);
      setGeneratedCode(code.toString());
      
      await sendOneTimeLoginCodeByEmail(newEmail, code);
      setEmailVerificationMessage('Code resent successfully!');
    } catch (error) {
      setEmailVerificationMessage('Error resending code. Please try again.');
    }
    
    setIsVerifyingEmail(false);
  };

  const resetState = () => {
    setNewEmail('');
    setIsEmailCodeSent(false);
    setEmailVerificationCode('');
    setEmailVerificationMessage('');
    setIsVerifyingEmail(false);
    setGeneratedCode('');
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  return (
    <View style={{ marginTop: 10 }}>
      {!isEmailCodeSent ? (
        <View>
          <TextInput
            style={{ height: 40, width: 300, backgroundColor: '#fff', padding: 5, marginBottom: 10, borderWidth: 1, borderColor: '#ccc', borderRadius: 5 }}
            placeholder="Enter your email address"
            placeholderTextColor="gray"
            value={newEmail}
            onChangeText={setNewEmail}
            textAlignVertical={"center"}
            maxLength={100}
            keyboardType="email-address"
            autoCapitalize="none"
          />
          <View style={{ flexDirection: 'row', marginBottom: 10 }}>
            <View style={{ marginRight: 10 }}>
              <SmallButton 
                theme="basic" 
                label={isVerifyingEmail ? "Sending..." : "Save"} 
                onPress={handleAddEmail} 
                isDisabled={isVerifyingEmail}
              />
            </View>
            <SmallButton 
              theme="secondary" 
              label="Cancel" 
              onPress={handleCancel} 
            />
          </View>
        </View>
      ) : (
        <View>
          <Text style={{ fontSize: 16, marginBottom: 10 }}>Enter the verification code sent to {newEmail}:</Text>
          <TextInput
            style={{ height: 40, width: 300, backgroundColor: '#fff', padding: 5, marginBottom: 10, borderWidth: 1, borderColor: '#ccc', borderRadius: 5 }}
            placeholder="Enter verification code"
            placeholderTextColor="gray"
            value={emailVerificationCode}
            onChangeText={setEmailVerificationCode}
            textAlignVertical={"center"}
            maxLength={10}
            keyboardType="number-pad"
          />
          <View style={{ flexDirection: 'row', marginBottom: 10 }}>
            <View style={{ marginRight: 10 }}>
              <SmallButton 
                theme="basic" 
                label={isVerifyingEmail ? "Verifying..." : "Save"} 
                onPress={handleVerifyEmailCode} 
                isDisabled={isVerifyingEmail}
              />
            </View>
            <View style={{ marginRight: 10 }}>
              <SmallButton 
                theme="secondary" 
                label={isVerifyingEmail ? "Sending..." : "Resend"} 
                onPress={handleResendEmailCode} 
                isDisabled={isVerifyingEmail}
              />
            </View>
            <SmallButton 
              theme="secondary" 
              label="Cancel" 
              onPress={handleCancel} 
            />
          </View>
        </View>
      )}
      {emailVerificationMessage ? (
        <Text style={{ color: emailVerificationMessage.includes('success') ? '#4CAF50' : '#674FE0', marginTop: 10, fontSize: 14 }}>
          {emailVerificationMessage}
        </Text>
      ) : null}
    </View>
  );
}
